package com.tixforgigs.common.core.util

import com.tixforgigs.common.core.extensions.tryCatchSentry
import io.ktor.http.*
import kotlinx.datetime.*
import kotlin.time.Clock
import kotlin.time.ExperimentalTime
import kotlin.time.Instant


@OptIn(ExperimentalTime::class)
object DateTimeUtil {

  val currentInstant get() = Clock.System.now()
  val currentTimestamp get() = Clock.System.now().toEpochMilliseconds()

  enum class Type {
    DATE, TIME, DATETIME
  }

  fun Instant.toDateTimeString(
    showDay: Boolean = true,
    showMonth: Boolean = true,
    showYear: Boolean = true,
    showHours: Boolean = true,
    showMinutes: Boolean = true,
    showSeconds: Boolean = true,
  ) = toLocalDateTime(TimeZone.currentSystemDefault())
    .toDateTimeString(
      showDay = showDay,
      showMonth = showMonth,
      showYear = showYear,
      showHours = showHours,
      showMinutes = showMinutes,
      showSeconds = showSeconds
    )

  fun Instant.toDateString(): String = toDateTimeString(
    showHours = false,
    showMinutes = false,
    showSeconds = false
  )

  fun Instant.roundOffToMinute(): Instant {
    val flooredSeconds = epochSeconds.floorDiv(60L).times(60L)
    return Instant.fromEpochSeconds(flooredSeconds)
  }

  fun Long.toDateTimeString(
    showDay: Boolean = true,
    showMonth: Boolean = true,
    showYear: Boolean = true,
    showHours: Boolean = true,
    showMinutes: Boolean = true,
    showSeconds: Boolean = true,
  ) = Instant.fromEpochMilliseconds(this)
    .toDateTimeString(
      showDay = showDay,
      showMonth = showMonth,
      showYear = showYear,
      showHours = showHours,
      showMinutes = showMinutes,
      showSeconds = showSeconds
    )

  fun String.toISO8601(): String? {
    val regex = Regex("\\d{4}-\\d{2}-\\d{2}$")
    return if (regex.matches(this)) {
      LocalDate.parse(this)
        .atTime(0, 0)
        .toInstant(TimeZone.currentSystemDefault())
        .toString()
    } else null
  }

  @Deprecated("Not needed anymore")
  fun String.parseRfc1123ToInstant(): Instant {
    return Instant.fromEpochMilliseconds(fromHttpToGmtDate().timestamp)
  }


  fun Instant.toInputValue(
    type: Type = Type.DATE,
    timeZone: TimeZone = TimeZone.currentSystemDefault()
  ) = when (type) {
    Type.DATE -> toLocalDateTime(timeZone).date.toString()
    Type.TIME -> toLocalDateTime(timeZone).time.toString()
    Type.DATETIME -> toLocalDateTime(timeZone).toString()
  }

  fun String.toInstant(): Instant? {
    val regex = Regex("\\d{4}-\\d{2}-\\d{2}$")
    return tryCatchSentry {
      if (regex.matches(this)) {
        LocalDate.parse(this)
          .atTime(0, 0)
          .toInstant(TimeZone.currentSystemDefault())
      } else {
        null
      }
    }.getOrNull()
  }

  fun LocalTime.toTimeString(): String {
    val hours = hour.twoDigits()
    val minutes = minute.twoDigits()
    val seconds = second.twoDigits()
    return if (hour > 0) "$hours:$minutes:$seconds" else "$minutes:$seconds"
  }


  private fun LocalDateTime.toDateTimeString(
    showDay: Boolean = true,
    showMonth: Boolean = true,
    showYear: Boolean = true,
  ): String {
    val day = day.twoDigits()
    val month = month.number.twoDigits()
    val year = year.toString()
    return "".plus(if (showDay) day else "")
      .plus(if (showMonth) "${if (showDay) "." else ""}$month" else "")
      .plus(if (showYear) "${if (showMonth) "." else ""}$year" else "")
  }

  private fun LocalDateTime.toDateTimeString(
    showDay: Boolean = true,
    showMonth: Boolean = true,
    showYear: Boolean = true,
    showHours: Boolean = true,
    showMinutes: Boolean = true,
    showSeconds: Boolean = true,
  ): String {
    val day = day.twoDigits()
    val month = month.number.twoDigits()
    val year = year.toString()
    val hours = hour.twoDigits()
    val minutes = minute.twoDigits()
    val seconds = second.twoDigits()
    return "".plus(if (showDay) day else "")
      .plus(if (showMonth) "${if (showDay) "." else ""}$month" else "")
      .plus(if (showYear) "${if (showMonth) "." else ""}$year" else "")
      .plus(if (showHours) " $hours" else "")
      .plus(if (showMinutes) ":$minutes" else "")
      .plus(if (showSeconds) ":$seconds" else "")
      .plus(if (showHours && showMinutes) " Uhr" else "")
  }

  private fun Int.twoDigits(): String {
    return this.toString().padStart(2, '0')
  }
}
